import { useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowDownIcon, ArrowUpIcon } from "../../components/Icons";
import { CANDIDATE_STATUS_LABELS } from "../../components/interviewList/candidateStatus";
import { TemplateCsvImportDialog } from "../../components/template/TemplateCsvImportDialog";
import { TemplateItemsViewTable } from "../../components/template/TemplateItemsViewTable";
import { InterviewerAssignDialog } from "../../components/term/InterviewerAssignDialog";
import { InterviewerListSection } from "../../components/term/InterviewerListSection";
import { ScheduleConfigDialog } from "../../components/term/ScheduleConfigDialog";
import { ScheduleConfigSection } from "../../components/term/ScheduleConfigSection";
import { TermInterviewListSection } from "../../components/term/TermInterviewListSection";
import { useTermInterviewerAssignment } from "../../components/term/useTermInterviewerAssignment";
import { Button } from "../../components/ui/button";
import { InterviewType } from "../../graphql/generated/graphql";
import {
  useCandidateMutations,
  useCandidates,
  useSponsor,
  useSponsorReminderSetting,
  useTemplateItems,
  useTemplateMutations,
  useTerm,
  useTermScheduleConfig,
  useTermTemplate,
} from "../../hooks";
import { calculateElapsedTimes } from "../../lib/templateItemElapsedTime";
import { ApplyTemplatePresetDialog } from "./components/ApplyTemplatePresetDialog";
import { BulkCandidateDialog } from "./components/BulkCandidateDialog";
import { SponsorReminderMailDialog } from "./components/SponsorReminderMailDialog";
import { TermInfoSection } from "./components/TermInfoSection";

export function TermDetail() {
  const navigate = useNavigate();
  const params = useParams();
  const termId = params.termId ?? null;

  // Data hooks
  const {
    term,
    loading,
    error: termError,
    refetch: refetchTerm,
  } = useTerm(termId);
  const { sponsor, error: sponsorError } = useSponsor(term?.sponsorId ?? null);
  const {
    candidates,
    error: candidatesError,
    refetch: refetchCandidates,
  } = useCandidates(termId);
  const {
    scheduleConfig,
    error: scheduleConfigError,
    refetch: refetchConfig,
  } = useTermScheduleConfig(termId);
  const { sponsorReminderSetting, refetch: refetchSponsorReminderSetting } =
    useSponsorReminderSetting(termId);
  const {
    interviewerDataLoadErrors,
    interviewerMutationError,
    interviewerListSectionProps,
    interviewerAssignDialogProps,
  } = useTermInterviewerAssignment(termId);
  const {
    template,
    error: templateError,
    refetch: refetchTemplate,
  } = useTermTemplate(termId);
  const {
    templateItems,
    error: templateItemsError,
    refetch: refetchTemplateItems,
  } = useTemplateItems(template?.templateId ?? null);

  // Query 失敗の表示：いずれか1件でも失敗していたらバナーで通知
  const dataLoadErrors = [
    termError,
    sponsorError,
    candidatesError,
    scheduleConfigError,
    templateError,
    templateItemsError,
    ...interviewerDataLoadErrors,
  ].filter((e): e is string => Boolean(e));

  // Mutation hooks（ターム情報の更新は TermInfoSection が独自に呼ぶので updateTerm は外す）
  const {
    sendInvitation,
    bulkSendInvitation,
    updateCandidate,
    deleteCandidate,
  } = useCandidateMutations();
  const { bulkCreateTemplateItems } = useTemplateMutations();
  // 編集保存の失敗が取り込みダイアログのエラー表示に混ざらないよう、用途ごとに state を分ける
  const {
    bulkCreateTemplateItems: importTemplateItems,
    loading: templateImportLoading,
    error: templateImportError,
  } = useTemplateMutations();

  // Dialog states
  const [configOpen, setConfigOpen] = useState(false);
  const [sponsorMailOpen, setSponsorMailOpen] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [templateOpen, setTemplateOpen] = useState(false);
  const [applyPresetOpen, setApplyPresetOpen] = useState(false);
  const [isEditingTemplate, setIsEditingTemplate] = useState(false);

  const [editingTemplateItems, setEditingTemplateItems] = useState<
    Array<{
      id?: string;
      category: string;
      timeEstimate: string;
      itemName: string;
      question: string;
      note: string;
      isRequired: boolean;
      order: number;
    }>
  >([]);

  const editingElapsedTimes = useMemo(
    () => calculateElapsedTimes(editingTemplateItems),
    [editingTemplateItems],
  );

  const handleBulkSendInvitation = async () => {
    const unsentCount = candidates.filter((c) => c.status === "UNSENT").length;
    if (unsentCount === 0) {
      alert("未送信の候補者がいません");
      return;
    }
    if (
      !termId ||
      !window.confirm(
        `未送信の候補者${unsentCount}名に日程調整依頼メールを送信しますか？`,
      )
    )
      return;

    try {
      const result = await bulkSendInvitation(termId);
      if (result) {
        alert(
          `送信成功: ${result.successCount}件\n送信失敗: ${result.failureCount}件`,
        );
        refetchCandidates();
      } else {
        alert("一括送信に失敗しました");
      }
    } catch {
      alert("一括送信に失敗しました");
    }
  };

  const handleSendInvitation = async (candidateId: string) => {
    if (!window.confirm("日程調整依頼メールを送信しますか？")) {
      return;
    }

    try {
      const result = await sendInvitation(candidateId);
      if (result?.success) {
        alert("メールを送信しました");
        refetchCandidates();
      } else {
        alert(result?.errorMessage || "メール送信に失敗しました");
      }
    } catch {
      alert("メール送信に失敗しました");
    }
  };

  const handleDeleteCandidate = async (candidateId: string) => {
    if (!window.confirm("削除しますか？")) return;
    try {
      const success = await deleteCandidate(candidateId);
      if (success) {
        refetchCandidates();
      } else {
        alert("削除に失敗しました");
      }
    } catch {
      alert("削除に失敗しました");
    }
  };

  const handleToggleKeyCandidate = async (
    candidateId: string,
    isKeyCandidate: boolean,
  ) => {
    try {
      const success = await updateCandidate({ candidateId, isKeyCandidate });
      if (success) {
        refetchCandidates();
      } else {
        alert("キーインタビュアー限定の変更に失敗しました");
      }
    } catch {
      alert("キーインタビュアー限定の変更に失敗しました");
    }
  };

  const handleTemplateSave = async () => {
    if (!template?.templateId) {
      alert("テンプレートが見つかりません");
      return;
    }
    try {
      const items = editingTemplateItems.map((item, index) => ({
        category: item.category,
        timeEstimate: item.timeEstimate || undefined,
        itemName: item.itemName,
        question: item.question || undefined,
        note: item.note || undefined,
        isRequired: item.isRequired,
        order: index,
      }));

      const result = await bulkCreateTemplateItems(template.templateId, items);
      if (result) {
        setIsEditingTemplate(false);
        refetchTemplate();
        refetchTemplateItems();
        alert("インタビューテンプレートを保存しました");
      } else {
        alert("テンプレートの保存に失敗しました");
      }
    } catch {
      alert("テンプレートの保存に失敗しました");
    }
  };

  const handleTemplateItemChange = (
    index: number,
    field: string,
    value: string | boolean,
  ) => {
    const newItems = [...editingTemplateItems];
    newItems[index] = { ...newItems[index], [field]: value };
    if (field === "itemName") {
      newItems[index].isRequired = (value as string).includes("⭐");
    }
    setEditingTemplateItems(newItems);
  };

  const handleTemplateItemAdd = () => {
    setEditingTemplateItems([
      ...editingTemplateItems,
      {
        category: "",
        timeEstimate: "",
        itemName: "",
        question: "",
        note: "",
        isRequired: false,
        order: editingTemplateItems.length,
      },
    ]);
  };

  const handleTemplateItemDelete = (index: number) => {
    if (!window.confirm("この行を削除しますか？")) return;
    const newItems = editingTemplateItems.filter((_, i) => i !== index);
    setEditingTemplateItems(newItems.map((item, i) => ({ ...item, order: i })));
  };

  const handleTemplateItemMove = (index: number, direction: "up" | "down") => {
    if (direction === "up" && index === 0) return;
    if (direction === "down" && index === editingTemplateItems.length - 1)
      return;

    const newItems = [...editingTemplateItems];
    const targetIndex = direction === "up" ? index - 1 : index + 1;
    [newItems[index], newItems[targetIndex]] = [
      newItems[targetIndex],
      newItems[index],
    ];
    setEditingTemplateItems(newItems.map((item, i) => ({ ...item, order: i })));
  };

  const getScheduleUrl = (token: string | null) => {
    if (!token) return "#";
    const baseUrl = typeof window !== "undefined" ? window.location.origin : "";
    return `${baseUrl}/employee_interview/schedule/${token}`;
  };

  if (loading) {
    return (
      <div className="min-h-[calc(100vh-200px)] bg-[var(--color-bg-subtle-gray)] p-6">
        <div className="p-10 text-center font-medium text-[var(--color-text-secondary)]">
          読み込み中...
        </div>
      </div>
    );
  }

  // termId が null = URL に termId が無いケース。term loading 完了後にこのガードを通る。
  if (!term || !termId) {
    return (
      <div className="min-h-[calc(100vh-200px)] bg-[var(--color-bg-subtle-gray)] p-6">
        <div className="p-10 text-center font-medium text-[var(--color-text-secondary)]">
          タームが見つかりません
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-[calc(100vh-200px)] bg-[var(--color-bg-subtle-gray)] p-6">
      <div className="w-full">
        {dataLoadErrors.length > 0 && (
          <div className="mb-4 rounded-xl border border-[var(--color-red-100)] bg-[var(--color-red-50)] p-4">
            <p className="font-medium text-[var(--color-red-600)]">
              データの一部の読み込みに失敗しました
            </p>
            <ul className="mt-2 list-disc pl-5 text-[var(--color-red-600)] text-sm">
              {dataLoadErrors.map((message) => (
                <li key={message}>{message}</li>
              ))}
            </ul>
          </div>
        )}
        {/* 操作の失敗は alert でも通知するが、定型文に丸められない理由はここにだけ出る */}
        {interviewerMutationError && (
          <div className="mb-4 rounded-xl border border-[var(--color-red-100)] bg-[var(--color-red-50)] p-4">
            <p className="font-medium text-[var(--color-red-600)] text-sm">
              {interviewerMutationError}
            </p>
          </div>
        )}
        {/* パンくずリスト */}
        <div className="mb-4">
          <nav className="flex items-center gap-2 text-[13px] text-[var(--color-text-secondary)]">
            <Link
              to="/employee_interview/sponsors"
              className="cursor-pointer text-[var(--color-primary-500)] no-underline hover:underline"
            >
              スポンサー
            </Link>
            {sponsor && (
              <>
                <span className="text-[#9ca3af]">/</span>
                <Link
                  to={`/employee_interview/sponsors/${sponsor.sponsorId}`}
                  className="cursor-pointer text-[var(--color-primary-500)] no-underline hover:underline"
                >
                  {sponsor.name}
                </Link>
              </>
            )}
            <span className="text-[#9ca3af]">/</span>
            <span className="font-medium text-[var(--color-text-primary)]">
              {term.title}
            </span>
          </nav>
        </div>

        {/* ヘッダーセクション */}
        <div className="mb-8 flex items-start justify-between">
          <div className="border-[var(--color-primary-500)] border-l-4 pl-5">
            <h1 className="mb-2 font-bold text-[32px] text-[var(--color-primary-500)]">
              {term.title}
            </h1>
            {sponsor && (
              <p className="font-medium text-[var(--color-text-secondary)] text-sm">
                スポンサー: {sponsor.name}
              </p>
            )}
          </div>
          <Button
            onClick={() =>
              navigate(`/employee_interview/terms/${termId}/report`)
            }
          >
            レポートを表示
          </Button>
        </div>

        <div className="flex flex-col gap-6">
          <TermInfoSection
            termId={termId}
            term={term}
            refetchTerm={refetchTerm}
          />

          <ScheduleConfigSection
            scheduleConfig={scheduleConfig}
            onEdit={() => setConfigOpen(true)}
          />

          {/* スポンサー向けリマインドメールカード */}
          <div className="overflow-hidden rounded-xl border border-[var(--color-border-default)] bg-white shadow-[0_2px_8px_rgba(0,0,0,0.05)]">
            <div className="flex items-center justify-between border-[var(--color-primary-500)] border-b-2 bg-[var(--color-bg-subtle-gray)] px-6 py-5">
              <h2 className="font-bold text-2xl text-[var(--color-primary-500)]">
                スポンサー向けリマインドメール
              </h2>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setSponsorMailOpen(true)}
                className="text-[var(--color-primary-500)]"
              >
                編集
              </Button>
            </div>
            <div className="p-6">
              <div className="flex flex-col gap-6">
                <div>
                  <p className="mb-1 font-medium text-[13px] text-[var(--color-text-secondary)]">
                    送信タイミング（
                    {sponsorReminderSetting?.enabled
                      ? "自動送信: 有効"
                      : "自動送信: 無効"}
                    ）
                  </p>
                  {sponsorReminderSetting &&
                  sponsorReminderSetting.conditions.length > 0 ? (
                    <ul className="flex flex-col gap-1">
                      {sponsorReminderSetting.conditions.map(
                        (condition, index) => (
                          <li
                            // 条件は順序で表示する固定リスト
                            // biome-ignore lint/suspicious/noArrayIndexKey: 表示専用の順序リスト
                            key={index}
                            className="font-medium text-[15px] text-[var(--color-text-primary)]"
                          >
                            {condition.sendDate
                              ? condition.sendDate
                              : `回答期限の${condition.daysBeforeDeadline ?? "—"}日前`}
                            ・完了率{condition.completionRateThreshold ?? "—"}
                            %未満で送信
                          </li>
                        ),
                      )}
                    </ul>
                  ) : (
                    <p className="font-medium text-[15px] text-[var(--color-text-primary)]">
                      条件未設定
                    </p>
                  )}
                  <p className="mt-1 text-[var(--color-text-secondary)] text-xs">
                    ※ 実際の自動送信は今後対応（現時点では条件の保存のみ）
                  </p>
                </div>
                <div>
                  <p className="mb-1 font-medium text-[13px] text-[var(--color-text-secondary)]">
                    送信対象（メールアドレス）
                  </p>
                  <p className="font-medium text-[15px] text-[var(--color-text-primary)]">
                    {sponsorReminderSetting &&
                    sponsorReminderSetting.recipientEmails.length > 0
                      ? sponsorReminderSetting.recipientEmails.join(", ")
                      : "未設定"}
                  </p>
                </div>
                <div>
                  <p className="mb-1 font-medium text-[13px] text-[var(--color-text-secondary)]">
                    件名
                  </p>
                  <p className="whitespace-pre-wrap break-words font-medium text-[15px] text-[var(--color-text-primary)]">
                    {sponsorReminderSetting?.title ?? "未設定"}
                  </p>
                </div>
                <div>
                  <p className="mb-1 font-medium text-[13px] text-[var(--color-text-secondary)]">
                    本文
                  </p>
                  <p className="whitespace-pre-wrap break-words rounded-md border border-[var(--color-border-default)] bg-[var(--color-bg-subtle-gray)] p-3 font-medium text-[15px] text-[var(--color-text-primary)]">
                    {sponsorReminderSetting?.description ?? "未設定"}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* インタビューテンプレートカード */}
          <div className="overflow-hidden rounded-xl border border-[var(--color-border-default)] bg-white shadow-[0_2px_8px_rgba(0,0,0,0.05)]">
            <div className="flex items-center justify-between border-[var(--color-primary-500)] border-b-2 bg-[var(--color-bg-subtle-gray)] px-6 py-5">
              <h2 className="font-bold text-2xl text-[var(--color-primary-500)]">
                インタビューテンプレート
              </h2>
              <div className="flex gap-2">
                {isEditingTemplate ? (
                  <>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        if (
                          window.confirm(
                            "編集をキャンセルしますか？変更は保存されません。",
                          )
                        ) {
                          setIsEditingTemplate(false);
                          refetchTemplate();
                          refetchTemplateItems();
                        }
                      }}
                    >
                      キャンセル
                    </Button>
                    <Button size="sm" onClick={handleTemplateSave}>
                      保存
                    </Button>
                  </>
                ) : (
                  <>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        if (templateItems) {
                          setEditingTemplateItems(
                            templateItems.map((item) => ({
                              id: item.templateItemId,
                              category: item.category,
                              timeEstimate: item.timeEstimate || "",
                              itemName: item.itemName,
                              question: item.question || "",
                              note: item.note || "",
                              isRequired: item.isRequired,
                              order: item.order,
                            })),
                          );
                        }
                        setIsEditingTemplate(true);
                      }}
                      className="text-[var(--color-primary-500)]"
                    >
                      編集
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setApplyPresetOpen(true)}
                      className="text-[var(--color-primary-500)]"
                    >
                      テンプレートから取り込む
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setTemplateOpen(true)}
                      className="text-[var(--color-primary-500)]"
                    >
                      CSVからインポート
                    </Button>
                  </>
                )}
              </div>
            </div>
            <div className="p-6">
              {isEditingTemplate ? (
                <div>
                  <div className="mb-4 flex items-center justify-between">
                    <div className="text-[var(--color-text-secondary)] text-sm">
                      項目数: {editingTemplateItems.length}件
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleTemplateItemAdd}
                      className="text-[var(--color-primary-500)]"
                    >
                      + 行を追加
                    </Button>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full border-collapse">
                      <thead>
                        <tr className="bg-[var(--color-bg-subtle-gray)]">
                          <th className="w-[60px] p-3 text-left font-semibold text-[var(--color-text-secondary)] text-xs">
                            操作
                          </th>
                          <th className="p-3 text-left font-semibold text-[var(--color-text-secondary)] text-xs">
                            カテゴリ
                          </th>
                          <th className="p-3 text-left font-semibold text-[var(--color-text-secondary)] text-xs">
                            時間目安
                          </th>
                          <th className="p-3 text-left font-semibold text-[var(--color-text-secondary)] text-xs">
                            経過時間
                          </th>
                          <th className="p-3 text-left font-semibold text-[var(--color-text-secondary)] text-xs">
                            項目
                          </th>
                          <th className="min-w-[300px] p-3 text-left font-semibold text-[var(--color-text-secondary)] text-xs">
                            質問/発話
                          </th>
                          <th className="min-w-[300px] p-3 text-left font-semibold text-[var(--color-text-secondary)] text-xs">
                            補足
                          </th>
                          <th className="w-[50px] p-3 text-left font-semibold text-[var(--color-text-secondary)] text-xs">
                            削除
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {editingTemplateItems.map((item, index) => (
                          <tr
                            key={index}
                            className="border-[var(--color-border-default)] border-b"
                          >
                            <td className="p-2">
                              <div className="flex flex-col gap-1">
                                <Button
                                  variant="ghost"
                                  size="icon-sm"
                                  onClick={() =>
                                    handleTemplateItemMove(index, "up")
                                  }
                                  disabled={index === 0}
                                  className="h-6 w-6"
                                >
                                  <ArrowUpIcon className="h-4 w-4" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="icon-sm"
                                  onClick={() =>
                                    handleTemplateItemMove(index, "down")
                                  }
                                  disabled={
                                    index === editingTemplateItems.length - 1
                                  }
                                  className="h-6 w-6"
                                >
                                  <ArrowDownIcon className="h-4 w-4" />
                                </Button>
                              </div>
                            </td>
                            <td className="p-2">
                              <input
                                type="text"
                                value={item.category}
                                onChange={(e) =>
                                  handleTemplateItemChange(
                                    index,
                                    "category",
                                    e.target.value,
                                  )
                                }
                                className="w-full min-w-[120px] rounded border border-[var(--color-border-default)] p-2 text-sm"
                              />
                            </td>
                            <td className="p-2">
                              <input
                                type="text"
                                value={item.timeEstimate}
                                onChange={(e) =>
                                  handleTemplateItemChange(
                                    index,
                                    "timeEstimate",
                                    e.target.value,
                                  )
                                }
                                placeholder="〜3min."
                                className="w-full min-w-[100px] rounded border border-[var(--color-border-default)] p-2 text-sm"
                              />
                            </td>
                            <td className="p-2 text-center text-[var(--color-text-secondary)] text-sm">
                              {editingElapsedTimes[index] > 0
                                ? `${editingElapsedTimes[index]}分`
                                : "-"}
                            </td>
                            <td className="p-2">
                              <input
                                type="text"
                                value={item.itemName}
                                onChange={(e) =>
                                  handleTemplateItemChange(
                                    index,
                                    "itemName",
                                    e.target.value,
                                  )
                                }
                                placeholder="⭐ 必須質問"
                                className="w-full min-w-[150px] rounded border border-[var(--color-border-default)] p-2 text-sm"
                              />
                            </td>
                            <td className="p-2">
                              <textarea
                                value={item.question}
                                onChange={(e) =>
                                  handleTemplateItemChange(
                                    index,
                                    "question",
                                    e.target.value,
                                  )
                                }
                                placeholder="インタビュアーの質問/発話"
                                rows={3}
                                className="w-full min-w-[300px] resize-y rounded border border-[var(--color-border-default)] p-2 text-sm"
                              />
                            </td>
                            <td className="p-2">
                              <textarea
                                value={item.note}
                                onChange={(e) =>
                                  handleTemplateItemChange(
                                    index,
                                    "note",
                                    e.target.value,
                                  )
                                }
                                placeholder="質問の意図etcに関する補足"
                                rows={3}
                                className="w-full min-w-[300px] resize-y rounded border border-[var(--color-border-default)] p-2 text-sm"
                              />
                            </td>
                            <td className="p-2">
                              <Button
                                variant="destructive"
                                size="icon-sm"
                                onClick={() => handleTemplateItemDelete(index)}
                              >
                                ×
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              ) : templateItems && templateItems.length > 0 ? (
                <TemplateItemsViewTable
                  items={templateItems.map((item) => ({
                    key: item.templateItemId,
                    category: item.category,
                    timeEstimate: item.timeEstimate,
                    itemName: item.itemName,
                    question: item.question,
                    note: item.note,
                    isRequired: item.isRequired,
                  }))}
                />
              ) : (
                <div className="py-8 text-center text-[#9ca3af]">
                  インタビューテンプレートが登録されていません
                  <div className="mt-4">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setIsEditingTemplate(true)}
                      className="text-[var(--color-primary-500)]"
                    >
                      新規作成
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </div>

          <InterviewerListSection {...interviewerListSectionProps} />

          {/* 候補者一覧カード */}
          <div className="overflow-hidden rounded-xl border border-[var(--color-border-default)] bg-white shadow-[0_2px_8px_rgba(0,0,0,0.05)]">
            <div className="flex items-center justify-between border-[var(--color-primary-500)] border-b-2 bg-[var(--color-bg-subtle-gray)] px-6 py-5">
              <h2 className="font-bold text-2xl text-[var(--color-primary-500)]">
                候補者一覧
              </h2>
              <div className="flex gap-2">
                <Button
                  variant="orange"
                  size="sm"
                  onClick={handleBulkSendInvitation}
                >
                  一括依頼送信
                </Button>
                <Button size="sm" onClick={() => setBulkOpen(true)}>
                  一括登録
                </Button>
              </div>
            </div>
            <div className="p-6">
              {candidates.length === 0 ? (
                <div className="p-8 text-center text-[var(--color-text-secondary)]">
                  候補者がありません
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full border-collapse">
                    <thead>
                      <tr className="bg-[var(--color-bg-subtle-gray)]">
                        <th className="p-3 text-left font-semibold text-[var(--color-text-secondary)] text-xs">
                          ID
                        </th>
                        <th className="p-3 text-left font-semibold text-[var(--color-text-secondary)] text-xs">
                          名前
                        </th>
                        <th className="p-3 text-left font-semibold text-[var(--color-text-secondary)] text-xs">
                          メール
                        </th>
                        <th className="p-3 text-left font-semibold text-[var(--color-text-secondary)] text-xs">
                          ステータス
                        </th>
                        <th className="whitespace-nowrap p-3 text-left font-semibold text-[var(--color-text-secondary)] text-xs">
                          キーインタビュアー限定
                        </th>
                        <th className="p-3 text-left font-semibold text-[var(--color-text-secondary)] text-xs">
                          URL
                        </th>
                        <th className="p-3 text-left font-semibold text-[var(--color-text-secondary)] text-xs">
                          操作
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {candidates.map((candidate) => (
                        <tr
                          key={candidate.candidateId}
                          className="border-[var(--color-border-default)] border-b"
                        >
                          <td className="p-3 text-sm">
                            {candidate.candidateId.slice(-8)}
                          </td>
                          <td className="p-3 text-sm">
                            {candidate.sponsorEmployee?.name ?? "-"}
                          </td>
                          <td className="p-3 text-sm">
                            {candidate.sponsorEmployee?.email ?? "-"}
                          </td>
                          <td className="p-3 text-sm">
                            {CANDIDATE_STATUS_LABELS[candidate.status] ||
                              candidate.status}
                          </td>
                          <td className="p-3">
                            <span
                              className="whitespace-nowrap rounded-md px-2.5 py-1 font-medium text-[12px]"
                              style={{
                                backgroundColor: candidate.isKeyCandidate
                                  ? "#fef3c7"
                                  : "#f3f4f6",
                                color: candidate.isKeyCandidate
                                  ? "#92400e"
                                  : "#9ca3af",
                              }}
                            >
                              {candidate.isKeyCandidate ? "限定" : "限定なし"}
                            </span>
                          </td>
                          <td className="p-3">
                            {candidate.uniqueToken && (
                              <a
                                href={getScheduleUrl(candidate.uniqueToken)}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-[#2563eb] text-sm no-underline hover:underline"
                              >
                                日程調整ページ
                              </a>
                            )}
                          </td>
                          <td className="p-3">
                            <div className="flex gap-2">
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() =>
                                  handleSendInvitation(candidate.candidateId)
                                }
                                disabled={candidate.status !== "UNSENT"}
                              >
                                {candidate.status !== "UNSENT"
                                  ? "送信済み"
                                  : "メール送信"}
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() =>
                                  handleToggleKeyCandidate(
                                    candidate.candidateId,
                                    !candidate.isKeyCandidate,
                                  )
                                }
                              >
                                {candidate.isKeyCandidate
                                  ? "限定を解除"
                                  : "限定にする"}
                              </Button>
                              <Button
                                variant="destructive"
                                size="sm"
                                onClick={() =>
                                  handleDeleteCandidate(candidate.candidateId)
                                }
                              >
                                削除
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>

          <TermInterviewListSection
            termId={termId}
            onInterviewCancelled={refetchCandidates}
          />
        </div>
      </div>

      <ScheduleConfigDialog
        open={configOpen}
        onOpenChange={setConfigOpen}
        termId={termId}
        termDurationMinutes={term?.durationMinutes ?? null}
        interviewType={InterviewType.Employee}
        companyName={sponsor?.name ?? null}
        interviewTitle={term?.title ?? null}
        scheduleConfig={scheduleConfig}
        onSaved={refetchConfig}
      />

      {termId && (
        <SponsorReminderMailDialog
          open={sponsorMailOpen}
          onOpenChange={setSponsorMailOpen}
          termId={termId}
          setting={sponsorReminderSetting}
          onSaved={refetchSponsorReminderSetting}
        />
      )}

      <BulkCandidateDialog
        open={bulkOpen}
        onOpenChange={setBulkOpen}
        termId={termId}
        onCreated={refetchCandidates}
      />

      <InterviewerAssignDialog {...interviewerAssignDialogProps} />

      <TemplateCsvImportDialog
        open={templateOpen}
        onOpenChange={setTemplateOpen}
        title="インタビューテンプレートをインポート"
        loading={templateImportLoading}
        error={templateImportError}
        onImport={async (items) => {
          if (!template?.templateId) {
            alert("テンプレートが見つかりません");
            return false;
          }
          const result = await importTemplateItems(template.templateId, [
            ...items,
          ]);
          return result !== null;
        }}
        onImported={() => {
          refetchTemplate();
          refetchTemplateItems();
        }}
      />

      <ApplyTemplatePresetDialog
        open={applyPresetOpen}
        onOpenChange={setApplyPresetOpen}
        termId={termId}
        onApplied={() => {
          refetchTemplate();
          refetchTemplateItems();
        }}
      />
    </div>
  );
}
